import type { FilterGroup } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  RedoFilterGroup,
  RedoFilterSection,
} from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { summarizeSupportTicketViewFilters } from "../filters/summarize-support-ticket-view-filters";
import { useSupportTicketAdvancedFilters } from "../filters/use-support-ticket-advanced-filters";
import { SupportTicketCollectionsBoundary } from "../providers/support-providers";

export interface SupportTicketViewHoverContentProps {
  description?: string;
  filterGroup: FilterGroup;
  title: string;
}

const SupportTicketViewHoverBody = memo(function SupportTicketViewHoverBody({
  description,
  filterGroup,
  title,
}: SupportTicketViewHoverContentProps) {
  const definitions = useSupportTicketAdvancedFilters();
  const filters = useMemo(
    () => summarizeSupportTicketViewFilters(filterGroup, definitions),
    [definitions, filterGroup],
  );

  return (
    <Flex
      data-testid="support-ticket-view-hover"
      dir="column"
      gap="sm"
      maxW="xxs"
      minW="none"
    >
      <Flex dir="column" gap="xs" minW="none">
        <Text fontSize="sm" fontWeight="semibold">
          {title}
        </Text>
        {description ? (
          <Text
            data-testid="support-ticket-view-hover-description"
            fontSize="sm"
            fontWeight="regular"
            textColor="tertiary"
          >
            {description}
          </Text>
        ) : null}
      </Flex>
      {filters.length > 0 && (
        <Flex gap="xs" minW="none" w="full" wrap="wrap">
          {filters.map((filter, index) => (
            <RedoFilterGroup
              Icon={filter.Icon}
              key={`${filter.propertyName}-${index}`}
              propertyName={filter.propertyName}
              query={
                <RedoFilterSection
                  color="secondary"
                  text={filter.operatorLabel}
                />
              }
              value={
                filter.valueLabel ? (
                  <RedoFilterSection text={filter.valueLabel} weight="medium" />
                ) : null
              }
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
});

export const SupportTicketViewHoverContent = memo(
  function SupportTicketViewHoverContent({
    description,
    filterGroup,
    title,
  }: SupportTicketViewHoverContentProps) {
    return (
      <SupportTicketCollectionsBoundary>
        <SupportTicketViewHoverBody
          description={description}
          filterGroup={filterGroup}
          title={title}
        />
      </SupportTicketCollectionsBoundary>
    );
  },
);
